import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import TextFilter from '../../../../commonComponents/lists/filters/TextFilter';
import FormInput from '../../../../commonComponents/forms/FormInput';
import DateRange from '../../../../commonComponents/forms/inputs/DateRange';
import CountryProp from '../../../../commonComponents/forms/Address/CountryProp';
import LoggedUserContext from '../../../../contextProviders/LoggedUserContext';
import translate from '../../../../commonComponents/translate';
import ConfigsContext from '../../../../contextProviders/ConfigsContext';

const COMPETENCIES_DOMAINS_EN = [
  'Lite',
  'Start',
  'Premium',
  'Elite',
  'VIP',
  'INNER CIRCLE',
];

function ReportFilters(props) {
  const { loggedUser } = useContext(LoggedUserContext);
  const { organization } = useContext(ConfigsContext);
  const { competenciesDomains, skillLevels } = organization || {};
  const { _id: loggedUserId } = loggedUser;
  const { t, filters, onFilterChange, onDateChange, countries = [] } = props;
  const {
    search,
    createDateStart,
    createDateEnd,
    shippingCountry,
    businessLicense,
    access,
    active,
    forecastLevel,
    level,
    sponsoredBy,
    referral,
  } = filters;

  const levels = useMemo(
    () =>
      Object.keys(skillLevels || {}).map(l => ({
        id: l,
        text: skillLevels[l].name,
      })),
    [skillLevels],
  );
  const inputProps = useCallback(
    field => {
      switch (field) {
        case 'shippingCountry':
          return {
            searchable: true,
            allowClear: true,
            items: countries.map(c => ({
              id: c.code,
              text: `${c.flagEmoji} ${c.name}`,
            })),
          };
        case 'businessLicense':
          return {
            allowClear: true,
            searchable: false,
            items: [
              { id: true, text: t('yes') },
              { id: false, text: t('no') },
            ],
          };
        case 'access':
          return {
            allowClear: true,
            searchable: false,
            items: competenciesDomains
              .filter(c => COMPETENCIES_DOMAINS_EN.includes(c.translations.en))
              .map(c => {
                let order = 0;
                switch (c.translations.en) {
                  case 'Lite':
                    order = 0;
                    break;
                  case 'Start':
                    order = 1;
                    break;
                  case 'Premium':
                    order = 2;
                    break;
                  case 'Elite':
                    order = 3;
                    break;
                  case 'VIP':
                    order = 4;
                    break;
                  case 'INNER CIRCLE':
                    order = 5;
                    break;
                  default:
                    break;
                }
                return {
                  id: c._id,
                  text: c.name,
                  order,
                };
              })
              .sort((a, b) => a.order - b.order),
          };
        case 'active':
          return {
            allowClear: true,
            searchable: false,
            items: [
              { id: true, text: t('yes') },
              { id: false, text: t('no') },
            ],
          };
        case 'forecastLevel':
          return {
            searchable: true,
            allowClear: true,
            items: levels,
          };
        case 'level':
          return {
            searchable: true,
            allowClear: true,
            items: levels,
          };
        case 'sponsoredBy':
          return {
            allowClear: true,
            url: '/user/table/network/list',
          };
        case 'referral':
          return {
            allowClear: true,
            url: `/user/table/list?sponsoredBy=${loggedUserId}`,
          };
        default:
          return {
            items: [],
          };
      }
    },
    [loggedUserId],
  );
  return (
    <div className="filters-wrapper">
      <TextFilter
        id="searchInput"
        onChange={onFilterChange('search')}
        placeholder={t('search')}
        value={search}
      />
      <DateRange
        id="dateRange"
        placeholder={t('shop.date')}
        startDate={createDateStart}
        endDate={createDateEnd}
        onChange={({ startDate, endDate }) => onDateChange(startDate, endDate)}
        onClear={() => onDateChange(null, null)}
      />
      <FormInput
        id="shippingCountry"
        value={shippingCountry}
        name="shippingCountry"
        placeholder={t('address_countryCode')}
        type="select"
        onChange={onFilterChange('shippingCountry')}
        inputProps={inputProps('shippingCountry')}
      />
      <FormInput
        id="businessLicense"
        value={businessLicense}
        name="businessLicense"
        placeholder={t('report.neofin.businessLicense')}
        type="select"
        onChange={onFilterChange('businessLicense')}
        inputProps={inputProps('businessLicense')}
      />
      <FormInput
        id="access"
        value={access}
        name="access"
        placeholder={t('access')}
        type="select"
        onChange={onFilterChange('access')}
        inputProps={inputProps('access')}
      />
      <FormInput
        id="active"
        value={active}
        name="active"
        placeholder={t('active')}
        type="select"
        onChange={onFilterChange('active')}
        inputProps={inputProps('active')}
      />
      <FormInput
        id="forecastLevel"
        value={forecastLevel}
        name="forecastLevel"
        placeholder={t('report.neofin.currentLevel')}
        type="select"
        onChange={onFilterChange('forecastLevel')}
        inputProps={inputProps('forecastLevel')}
      />
      <FormInput
        id="level"
        value={level}
        name="level"
        placeholder={t('report.neofin.levelMax')}
        type="select"
        onChange={onFilterChange('level')}
        inputProps={inputProps('level')}
      />
      <FormInput
        id="sponsoredBy"
        name="sponsoredBy"
        placeholder={t('sponsor')}
        value={sponsoredBy}
        onChange={onFilterChange('sponsoredBy')}
        type="users"
        inputProps={inputProps('sponsoredBy')}
      />
      <FormInput
        id="referral"
        name="referral"
        placeholder={t('referral')}
        value={referral}
        onChange={onFilterChange('referral')}
        type="users"
        inputProps={inputProps('referral')}
      />
    </div>
  );
}

ReportFilters.propTypes = {
  t: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onDateChange: PropTypes.func.isRequired,
  filters: PropTypes.shape({
    search: PropTypes.string,
    createDateStart: PropTypes.number,
    createDateEnd: PropTypes.number,
    shippingCountry: PropTypes.string,
    businessLicense: PropTypes.string,
    access: PropTypes.string,
    active: PropTypes.bool,
    forecastLevel: PropTypes.number,
    level: PropTypes.number,
    sponsoredBy: PropTypes.string,
    referral: PropTypes.string,
  }).isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType)),
};

ReportFilters.defaultProps = {
  countries: [],
};

export default translate(ReportFilters);
